/**
 * Cumple — Página de solicitud de demo.
 *
 * Lead capture para reemplazar el flujo freemium. El form arma un
 * mailto: con los datos pre-llenados y abre el cliente del usuario.
 * Cuando Sprint 9 conecte un endpoint /api/v1/public/demo-request,
 * cambiar el handleSubmit por un POST.
 *
 * Tono: profesional, enterprise, sin promesas freemium.
 */
import { type FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';

import { ApiError, apiRequest } from '../api/client.js';

interface DemoRequestResponse {
  ok: boolean;
  id: string;
  message: string;
}

const SECTORS = [
  'Auditoría externa / firma',
  'Auditoría interna corporativa',
  'Compliance / oficial de cumplimiento',
  'Aduanas / OEA',
  'Gobierno / NORTIC',
  'Banca / SB RD',
  'Telecom / INDOTEL',
  'Manufactura / BPM',
  'Inocuidad / HACCP',
  'Otro',
];

const TEAM_SIZES = [
  '1–3 auditores',
  '4–10 auditores',
  '11–25 auditores',
  '26–50 auditores',
  '50+ auditores',
];

export function DemoPage(): JSX.Element {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [role, setRole] = useState('');
  const [sector, setSector] = useState(SECTORS[0]);
  const [teamSize, setTeamSize] = useState(TEAM_SIZES[0]);
  const [country, setCountry] = useState('República Dominicana');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const trimmedNotes = notes.trim();
      await apiRequest<DemoRequestResponse>('/api/v1/public/demo-request', {
        method: 'POST',
        body: {
          name: name.trim(),
          email: email.trim().toLowerCase(),
          company: company.trim(),
          role: role.trim(),
          sector: sector ?? undefined,
          team_size: teamSize ?? undefined,
          country: country.trim() || undefined,
          ...(trimmedNotes ? { notes: trimmedNotes } : {}),
          source: 'cumpleapp.com/demo',
        },
        signalAuthErrors: false,
      });
      setSent(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        setError('Datos inválidos. Revisa los campos del formulario.');
      } else if (err instanceof ApiError) {
        setError(`Error al enviar (${err.status}). Por favor intenta de nuevo en unos minutos.`);
      } else {
        setError('Error de red. Verifica tu conexión e intenta de nuevo.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="bg-white min-h-screen">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-6 py-4 flex items-center justify-between">
          <Link to="/pricing" className="flex items-center gap-2.5">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-brand-700 text-white font-bold text-sm">
              C
            </span>
            <span className="font-semibold text-brand-900 text-base">Cumple</span>
          </Link>
          <Link to="/pricing" className="text-sm font-medium text-slate-600 hover:text-brand-900">
            ← Volver al sitio
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-16 grid md:grid-cols-2 gap-12 items-start">
        {/* Left column — context */}
        <section className="space-y-8 md:pr-8">
          <div className="space-y-4">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-brand-700">
              DEMO PERSONALIZADA
            </p>
            <h1 className="text-3xl md:text-4xl font-bold text-brand-900 tracking-tight leading-tight">
              Agenda 30 minutos con nuestro equipo
            </h1>
            <p className="text-base text-slate-600 leading-relaxed">
              Te mostramos Cumple aplicado a tu sector y normas específicas. Sin discurso de
              marketing, directo a la herramienta y al ROI. Si encaja, hablamos de implementación.
            </p>
          </div>

          <div className="space-y-4 border-t border-slate-200 pt-6">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
              QUÉ INCLUYE LA DEMO
            </p>
            <ul className="space-y-3">
              {[
                'Tour del producto enfocado a tu sector (ISO, OEA, NORTIC, BPM, etc.)',
                'Caso de uso real: un levantamiento de campo de principio a fin',
                'Estimado de ROI según tamaño de equipo y número de engagements',
                'Plan de implementación (Starter, Team, Pro o Enterprise)',
                'Preguntas técnicas: residencia de datos, SSO, integraciones',
              ].map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-3 text-sm text-slate-600 leading-relaxed"
                >
                  <svg
                    className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="space-y-4 border-t border-slate-200 pt-6">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
              CONTACTO DIRECTO
            </p>
            <p className="text-sm text-slate-600 leading-relaxed">
              También puedes escribir directo a{' '}
              <a
                href="mailto:ventas@mssprd.com"
                className="font-semibold text-brand-700 hover:underline"
              >
                ventas@mssprd.com
              </a>{' '}
              o llamar a{' '}
              <a href="tel:+18092000000" className="font-semibold text-brand-700 hover:underline">
                +1 (809) 200-0000
              </a>{' '}
              de lunes a viernes 9am–6pm AST.
            </p>
          </div>
        </section>

        {/* Right column — form */}
        <section className="rounded-2xl border border-slate-200 bg-slate-50 p-8">
          {sent ? (
            <div className="space-y-4 text-center py-8">
              <svg
                className="h-12 w-12 text-emerald-500 mx-auto"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
              <h2 className="text-xl font-semibold text-brand-900">¡Solicitud recibida!</h2>
              <p className="text-sm text-slate-600 leading-relaxed">
                Te contactaremos en menos de <strong>24 horas hábiles</strong> para coordinar la
                demo personalizada. Si es urgente, escribe directo a{' '}
                <a
                  href="mailto:ventas@mssprd.com"
                  className="font-semibold text-brand-700 hover:underline"
                >
                  ventas@mssprd.com
                </a>
                .
              </p>
              <Link
                to="/pricing"
                className="inline-block mt-4 text-sm font-semibold text-brand-700 hover:underline"
              >
                ← Volver al sitio
              </Link>
            </div>
          ) : (
            <form
              onSubmit={(e) => {
                void handleSubmit(e);
              }}
              className="space-y-4"
              noValidate
            >
              <h2 className="text-xl font-semibold text-brand-900 mb-2">
                Cuéntanos sobre tu equipo
              </h2>

              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 sm:col-span-1">
                  <label htmlFor="name" className="block text-xs font-semibold text-slate-700 mb-1">
                    Nombre *
                  </label>
                  <input
                    id="name"
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
                  />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <label htmlFor="role" className="block text-xs font-semibold text-slate-700 mb-1">
                    Cargo *
                  </label>
                  <input
                    id="role"
                    type="text"
                    required
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    placeholder="Socio, CAE, Compliance Officer…"
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-xs font-semibold text-slate-700 mb-1">
                  Email corporativo *
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
                />
              </div>

              <div>
                <label
                  htmlFor="company"
                  className="block text-xs font-semibold text-slate-700 mb-1"
                >
                  Empresa *
                </label>
                <input
                  id="company"
                  type="text"
                  required
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label
                    htmlFor="sector"
                    className="block text-xs font-semibold text-slate-700 mb-1"
                  >
                    Sector
                  </label>
                  <select
                    id="sector"
                    value={sector}
                    onChange={(e) => setSector(e.target.value)}
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
                  >
                    {SECTORS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="size" className="block text-xs font-semibold text-slate-700 mb-1">
                    Tamaño equipo
                  </label>
                  <select
                    id="size"
                    value={teamSize}
                    onChange={(e) => setTeamSize(e.target.value)}
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
                  >
                    {TEAM_SIZES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label
                  htmlFor="country"
                  className="block text-xs font-semibold text-slate-700 mb-1"
                >
                  País
                </label>
                <input
                  id="country"
                  type="text"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
                />
              </div>

              <div>
                <label htmlFor="notes" className="block text-xs font-semibold text-slate-700 mb-1">
                  Notas (opcional)
                </label>
                <textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  maxLength={1000}
                  placeholder="Normas específicas, casos de uso, timing…"
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
                />
              </div>

              {error && (
                <div
                  role="alert"
                  className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700"
                >
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || !name || !email || !company || !role}
                className="w-full rounded-lg bg-brand-700 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-brand-700/90 disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                {submitting ? 'Enviando...' : 'Solicitar demo'}
              </button>

              <p className="text-xs text-slate-400 text-center pt-2">
                Al enviar aceptas que MSSPRD te contacte por email o teléfono. Tu información no se
                comparte con terceros.
              </p>
            </form>
          )}
        </section>
      </main>
    </div>
  );
}
